<template lang="pug">
  v-flex
    template-picker.pa-0(
      communicationType="Text"
      v-model="templatePickerVisible"
      :useSlideDrawer="true"
      :template="desiredTemplate"
      @change="onTemplateChange"
    )
    custom-number-dialog(
      :value="showSelectCustomNumberDialog"
      @close="showSelectCustomNumberDialog = false"
    )
    register-brand-modal(
      :value="showRegisterBrandModal"
      :no-skip="true"
      :new-version="true"
      @close="onCloseRegisterBrandModal"
    )
    sms-compliance-blocked-dialog(
      :value="showSmsComplianceBlockedDialog"
      @close="showSmsComplianceBlockedDialog = false"
    )
    //- Drawer to display video library and video preview/edit
    slide-drawer(
      :confirm-dismiss="confirmDismiss"
      ref="slide-drawer"
      v-model="showSlideDrawer"
      @input="handleReset"
    )
      template(slot="title") {{ showVideoLibrary ? 'Video Library' : 'Video Details' }}
      template(slot="content")
        video-library(
          @click-ld-video="handleShowVideo"
          @edit-ld-video="handleShowVideo"
          @insert-ld-video="insertLdVideo"
          communicationType="text"
          v-if="showVideoLibrary"
          ref="videoLibrary"
        )

        chat-video-detail.mx-4.mt-4.pr-5(
          :chatVideo="videoClicked"
          :isVideoEditable="isVideoEditable"
          :showVideoDetail="showVideoDetail"
          @modified:video="confirmDismiss = true"
          is-email-composer
          v-if="showVideoDetail"
        )
      template(slot="actions")
        v-btn(
          color="primary"
          outline
          v-if="showVideoDetail && !isVideoEditable"
          @click="handleActivateVideoEdit"
        ) Edit

        v-btn.primary(
          v-if="showVideoDetail && isVideoEditable"
          @click="handleVideoUpdate(videoClicked)"
        ) Save

        v-btn(
          v-if="showVideoDetail && !isVideoEditable"
          color="primary"
          @click="insertLdVideo(videoClicked)"
        ) Select

    v-card.main-card
      //- Title bar section to display the contact details
      v-card-title(v-if="hasConversations || isContactChat")
        v-layout
          v-flex(row wrap :class="[isContactChat ? 'xs8' : 'xs12']")
            v-layout
              v-flex.avatar-content(xs1)
                a(v-bind:href="'/contacts/' + contactDetails.id" target="_blank")
                  liondesk-avatar(
                    :color="contactDetails | getFullName | getInitials | generateRandomColor('dark')"
                    :text="contactDetails | getFullName | getInitials | replaceBlankName"
                    text-classes="white--text subheading"
                    size="30px"
                  )
              v-flex.mt-2.pr-0(xs11)
                span
                  a(v-bind:href="'/contacts/' + contactDetails.id" target="_blank") {{ contactDetails | getFullName | replaceBlankName }}
                span.pl-3(v-if="contactDetails.mobile_phone || phoneNumber") {{ contactDetails.mobile_phone || phoneNumber }}
                span.pl-3
                  v-tooltip(bottom)
                    span {{ tooltipFromStatus }}
                    v-icon(:style="styleFromStatus" slot="activator") {{ iconFromStatus }}
            p.ml-2.mt-2.red--text.caption.font-weight-bold(v-if="shouldDisplayNoPhoneMsg") It looks like the contact does not have a phone number. Please update the contact information to send a text message.
          v-flex.pl-0(row wrap :class="[isContactChat ? 'xs4' : 'xs8 sm9']")
            mute-lead-assist(:current-user="currentUser" :contact="contactDetails")
      //- Displays the list of chats
      v-card-text
        .pa-3(
          ref="messagesList"
          :class="hasConversations || isContactChat ? 'messages-list' : 'missing-messages'"
        )
          v-progress-circular(indeterminate color="primary" v-if="isLoading")
          v-layout.missing__conversations(
            row
            wrap
            align-center
            justify-content-center
            v-if="conversations && !conversations.length && !isContactChat"
          )
            v-flex.text-xs-center
              .headline.pb-3 You have no text conversations
              v-btn.primary(flat @click="$emit('compose-new-text')") Compose a Text
          //- Displays the load more button
          v-flex.text-lg-center(v-else)
            v-btn(v-if="hasMoreChatTextsToLoad" @click="$emit('load-more-callback')" flat) Load More

          section.message.pb-3(
            v-if="value && value.length"
            :class="message.direction"
            v-for="message in chats"
          )
            chat-message(
              :message="message"
              :contactDetails="contactDetails"
              :onVideoClicked="handleShowVideo"
            )
        //- Text area to write custom message
        liondesk-content-editor.mt-3(
          data-cy="text-body"
          v-if="hasConversations || isContactChat"
          :outline-field="true"
          actions-bar
          label="Insert text message here"
          field-type="textarea"
          ref-name="messageField"
          ref="messageField"
          v-model="textMessage"
          rows=2
          :hideActivator="true"
        )
          template(slot="choose-template")
            v-btn.action-button(
              flat
              @click="templatePickerVisible = true"
              data-cy="choose-template-button"
            ) Choose a Template

          template(slot="insert-video")
            v-btn.action-button(flat data-cy="liondesk-video-button" @click="handleVideoLibrary") Insert Video

          template(slot="insert-content")
            v-btn.action-button(flat data-cy="liondesk-content-button") Insert Content

          template(slot="insert-image")
            v-btn.action-button(flat data-cy="liondesk-image-button") Insert Image

        //- Simple send button that sends the message
        template.text-xs-right(v-if="hasConversations || !isContactChat")
          text-sending-options(
            padding="pt-0"
            recurrenceInputSize="xs6"
            :sending-option-dialog-out="sendingTextOptionsDialog"
            :showSend="false"
            @close-sending-dialog="sendingTextOptionsDialog = false"
            @send-message-callback="sendMessage"
            @change-sending-options="changeSendingOption"
            isChatHistory
          )

          v-layout(justify-end)
            v-btn.primary.ld-gated-feature(
              v-if="gatedAction === gatedActions.ComplianceViolationBlocked"
              slot="activator"
              :disabled="isSendButtonDisabled"
              @click="showSmsComplianceBlockedDialog = true"
            ) Send
            ld-gated-button.primary(
              v-else-if="gatedAction === gatedActions.NoAction"
              :disabled="isSendButtonDisabled"
              :feature="LdFeatures.TextMessaging"
              @click="onSendClick"
            ) Send
            v-btn.primary.ld-gated-feature(
              v-else-if="gatedAction === gatedActions.SetCustomNumber"
              :disabled="isSendButtonDisabled"
              @click="showSelectCustomNumberDialog = true"
            ) Send
            v-btn.primary(
              v-else-if="gatedAction === gatedActions.Register10DLC && shouldSendLeadsPlus"
              :disabled="isSendButtonDisabled"
              @click="onPending10DLCSend()"
            ) Send
            v-btn.primary.ld-gated-feature(
              v-else-if="gatedAction === gatedActions.Register10DLC"
              :disabled="isSendButtonDisabled"
              @click="onPending10DLCSend()"
            ) Send
            v-btn.primary.ld-gated-feature(
              v-else-if="gatedAction === gatedActions.Blocked"
              :disabled="true"
            ) Send
        v-snackbar(:value="actionError" color="error")
          div {{ actionErrorMessage }}

        v-snackbar(:value="actionSuccess" color="success")
          div {{ actionSuccessMessage }}
        v-snackbar(:value="conversationsErrorMessage" color="error")
          div {{ conversationsErrorMessage }}
        v-snackbar(:value="conversationsSuccessMessage" color="success")
          div {{ conversationsSuccessMessage }}
</template>

<script>
import { mapMutations, createNamespacedHelpers } from 'vuex';
import generateRandomColor from '@/Core/utilities/randomColorGenerator';
import getFullName from '@/Core/filters/getFullName';
import getInitials from '@/Core/filters/getInitials';
import LiondeskAvatar from '@/Core/components/LiondeskAvatar';
import LiondeskContentEditor from '@/Communications/components/LiondeskContentEditor';
import Media from '@/Media/mixins/media';
import replaceBlankName from '@/Core/filters/replaceBlankName';
import SlideDrawer from '@/Core/components/SlideDrawer';
import TemplatePicker from '@/Campaigns/components/TemplatePicker';
import toggleSlideDrawer from '@/Core/mixins/toggle-slide-drawer';
import VideoLibrary from '@/Media/components/VideoLibrary';
import MuteLeadAssist from '@/Communications/components/MuteLeadAssist';
import { LdGatedButton, LdFeatures } from 'lion-vue';
import { formatInternationalPhoneNumber } from '@/Core/utilities/formatInternationalPhoneNumber';
import TextSendingOptions from '@/Text/components/TextSendingOptions';
import CustomNumberDialog from '@/Core/components/CustomNumberDialog';
import RegisterBrandModal from '@/Core/components/RegisterBrandModal';
import { REGISTRATION_STATES } from '../config/registrationStatus';
import { EventBus } from '@/event-bus';
import { soleProprietor } from '@/Text/config/consoleSteps';

import sendSMSGate from '@/Core/mixins/send-sms-gate';
import { GATED_ACTIONS } from '@/Core/config/gated-actions';
import {
  STATUS,
  STATUS_ICON,
  STATUS_COLOR,
  BUTTON_TOOLTIP,
} from '@/Contacts/config/profile-optin-status';
import ChatMessage from './ChatMessage';
import ChatVideoDetail from './ChatVideoDetail';
import { STEPS_SOLE_PROPRIETOR } from '@/a2p/config';
import { SEND_OPTIONS } from '@/Communications/config';

const {
  mapState: mapConversationsState,
  mapActions: mapConversationsActions,
  mapGetters: mapConversationsGetters,
  mapMutations: mapConversationsMutations,
} = createNamespacedHelpers('conversations');

const { mapState: mapTemplatesState, mapActions: mapTemplatesActions } =
  createNamespacedHelpers('templates');

const { mapState: mapCampaignActionsState } = createNamespacedHelpers('campaignAction');

const { mapState: mapVideosState, mapActions: mapVideosActions } =
  createNamespacedHelpers('videos');

const { mapState: mapUserInformationState, mapGetters: mapUserInformationGetters } =
  createNamespacedHelpers('userInformation');

const { mapState: mapPaymentState, mapActions: mapPaymentActions } = createNamespacedHelpers('payment');

const { mapActions: mapSoleProprietorACtions, mapState: mapSoleProprietorState } = createNamespacedHelpers(
  'registerSoleProprietorA2p',
);

const solePropCloseAction = 'chat';

const SmsComplianceBlockedDialog = () =>
  import(/* webpackChunkName: 'SmsComplianceBlockedDialog' */ '@/Core/components/SmsComplianceBlockedDialog');

export default {
  components: {
    LdGatedButton,
    ChatMessage,
    ChatVideoDetail,
    LiondeskAvatar,
    LiondeskContentEditor,
    MuteLeadAssist,
    SlideDrawer,
    TemplatePicker,
    VideoLibrary,
    TextSendingOptions,
    CustomNumberDialog,
    RegisterBrandModal,
    SmsComplianceBlockedDialog,
  },
  props: {
    currentUser: {
      type: Object,
      default: () => {
        return {};
      },
    },
    // Callback when the send message function is hit.
    sendMessageCallback: Function,

    // Controls whether the send button should be disabled or not
    sendButtonDisabled: Boolean,

    // Controls loading from the outside, given the pattern has async calls outside :/
    isLoading: Boolean,

    // The details for a particular conversation's contact
    initialContactInfo: Object,

    // list of conversations,
    conversations: Array,

    // The chat history that we want to display
    value: Array,

    isContactChat: Boolean,

    phoneNumber: String,
  },
  mixins: [Media, toggleSlideDrawer, sendSMSGate],
  data() {
    return {
      LdFeatures,
      // The desired text template chosen
      desiredTemplate: {},
      // Stores the modified contact details passed in from the props
      contactDetails: { mobile_phone: null },
      // Stores the list of chats, which the inverse of the prop 'value'
      chats: [],
      templatePickerVisible: false,
      showVideoDetail: false,
      // The desired template we want to show in the composer
      templateFolders: [],
      sendingTextOptionsDialog: false,
      sendOptionRadioValue: 1,
      showSelectCustomNumberDialog: false,
      showRegisterBrandModal: false,
      contactOptInStatus: '',
      contactOptInDenied: false,
      gatedActions: GATED_ACTIONS,
      shouldDisplayNoPhoneMsg: false,
      showSmsComplianceBlockedDialog: false,
    };
  },

  filters: {
    getInitials, // Filter to get a current user's initials
    generateRandomColor, // Filters to generate a random color based on the string given
    getFullName,
    replaceBlankName,
  },

  computed: {
    ...mapUserInformationState(['user']),
    ...mapTemplatesState(['isEditing', 'body']),
    ...mapConversationsState([
      'hasMoreConversationsToLoad',
      'hasMoreChatTextsToLoad',
      'textMessageElements',
      'has10dlcRegister',
      'contactsOptInStatuses',
      'isRegistration10dlcIncomplete',
    ]),
    ...mapCampaignActionsState([
      'actionError',
      'actionErrorMessage',
      'actionSuccess',
      'actionSuccessMessage',
    ]),
    ...mapConversationsGetters(['chatHistoryLimit', 'isSendButtonDisabled']),
    ...mapUserInformationGetters(['shouldGoToLeadsPlusFlow']),
    ...mapVideosState(['videos']),
    ...mapConversationsState([
      'conversationsErrorMessage',
      'conversationsSuccessMessage',
      'registrationStatus10dlc',
      'registrationDate10dlc',
      'registrationType10dlc',
      'isLeadsPlusUser',
      'isLeadsPlusContact',
    ]),
    ...mapPaymentState(['addRegisterFeeAddonsSuccess']),
    ...mapSoleProprietorState(['registrationModalClose']),
    isOnFirstPageOfChatHistory() {
      return this.value && this.value.length <= this.chatHistoryLimit;
    },
    hasConversations() {
      return this.conversations && this.conversations.length;
    },
    textMessage: {
      get() {
        return this.textMessageElements.body;
      },
      set(value) {
        this.setNewTextMessage(value);
      },
    },
    styleFromStatus() {
      if (this.contactOptInStatus === STATUS.approved) return { color: STATUS_COLOR.approved };
      if (this.contactOptInStatus === STATUS.denied) return { color: STATUS_COLOR.denied };
      return { color: STATUS_COLOR.pending };
    },
    iconFromStatus() {
      if (this.contactOptInStatus === STATUS.approved) return STATUS_ICON.approved;
      if (this.contactOptInStatus === STATUS.denied) return STATUS_ICON.denied;
      return STATUS_ICON.pending;
    },
    tooltipFromStatus() {
      if (this.contactOptInStatus === STATUS.approved) return BUTTON_TOOLTIP.approved;
      if (this.contactOptInStatus === STATUS.denied) return BUTTON_TOOLTIP.denied;
      return BUTTON_TOOLTIP.pending;
    },
    // simplify after Aug 31
    canSendSMS() {
      const limitDate = new Date(2023, 8, 1);
      if (new Date() > limitDate) {
        return this.registrationStatus10dlc === 'campaign_completed';
      }
      const targetDate = new Date(Date.UTC(2023, 6, 20)); // July 20, 2023
      targetDate.setUTCHours(0, 0, 0, 0);
      const userCreatedAt = Date.parse(this.user.created_at);
      return new Date(userCreatedAt) < targetDate;
    },
    shouldSendLeadsPlus() {
      return this.shouldGoToLeadsPlusFlow && this.isLeadsPlusUser && this.isLeadsPlusContact && this.sendOptionRadioValue === SEND_OPTIONS.Immediate;
    },
  },

  watch: {
    contactsOptInStatuses(value) {
      if (value.length > 1) {
        this.contactOptInDenied = false;
        this.contactOptInStatus = '';
        return;
      }
      this.contactOptInDenied = value[0].status === STATUS.denied;
      this.contactOptInStatus = value[0].status;
    },
    desiredTemplate(value) {
      this.setNewTextMessage(value.body);
    },
    /*
     * This watches if there are any changes to the template body
     */
    newTextMessage(value) {
      if (value && value !== this.desiredTemplate.body) {
        // Sets the editing boolean to true, indicating that the body has been modified.
        this.setEditing(true);
      } else {
        this.setEditing(false);
      }
      this.setBody(value);
    },
    // Changes the internal representation of contact details
    initialContactInfo: {
      handler(value) {
        this.shouldDisplayNoPhoneMsg = !(
          value && 
          value.contact && 
          value.contact.mobile_phone && 
          value.contact.mobile_phone.length > 0
        );
        
        this.contactDetails = value ? { ...value.contact } : null;
        this.newTextMessage = '';
      },
      deep: true,
    },
    /**
     * Every time the prop changes, append the mobile_phone number from the list of chats
     * because mobile_phone number is not stored in the contact details
     * Also, concatenates the reversed value to the list of chats.
     * The reverse is important as we want to display the most recent message at the bottom
     */
    value: {
      async handler(value) {
        if (value && value.length) {
          // The mobile mobile_phone is attached to the `texts` endpoint, hence can only be obtained
          // after the list of texts is present
          const userTimezone = this.user.timezone;
          const contactExists = value[0].contact;
          const phoneNumber = contactExists ? value[0].contact.mobile_phone : value[0].mobile_phone;
          
          this.shouldDisplayNoPhoneMsg = !phoneNumber;
          
          this.contactDetails = {
            ...this.contactDetails,
            mobile_phone: formatInternationalPhoneNumber(phoneNumber, userTimezone),
          };
          this.chats = [].concat(value).reverse();
        } else {
          this.chats = [];
          this.contactDetails = {
            ...this.contactDetails,
          };
        }
      },
      deep: true,
    },
    async contactDetails(value) {
      if (value) {
        if (value.mobile_phone) {
          this.shouldDisplayNoPhoneMsg = !(value.mobile_phone.length > 0);
        }
        this.setNewContactMessage(value);
        await this.checkLeadsPlusContact(this.contactDetails.id);
      }
    },
    addRegisterFeeAddonsSuccess: {
      handler(value) {
        if (!value) { return };
        this.setStep(STEPS_SOLE_PROPRIETOR.STATUS);
        this.setShowRegistrationFlowModal({ bool: true, action: this.registrationModalClose });
        this.setAddRegisterFeeAddonsSuccess(false);
      },
    },
  },
  async created() {
    this.clearErrors();
    this.clearSuccess();

    const query = {
      communication_type: 'text',
      $sort: { created_at: -1 },
    };

    this.findVideos(query);
    if (!this.has10dlcRegister || this.isRegistration10dlcIncomplete) {
      await this.checkFor10dlcRegister();
    }
    await this.getLeadsPlusUserInfo();
    EventBus.$on('sole-prop-modal-close', data => {
      if (data === solePropCloseAction && this.canSendSMS) {
        this.onSendClick();
      }
    });
  },
  async mounted() {
    if (this.desiredTemplate) {
      this.newTextMessage = this.desiredTemplate.body ? this.desiredTemplate.body : '';
    }
    this.templateFolders = await this.getTemplateFolders();
  },

  updated() {
    if (this.$refs.messagesList && this.isOnFirstPageOfChatHistory) {
      this.$refs.messagesList.scrollTop = this.$refs.messagesList.scrollHeight;
    }
  },

  methods: {
    ...mapMutations('templates', ['setBody', 'setEditing', 'clearErrors', 'clearSuccess']),
    ...mapConversationsMutations(['setNewTextMessage', 'setNewContactMessage', 'clearTextImages']),
    ...mapTemplatesActions(['getTemplateFolders', 'setTemplatesError']),
    ...mapVideosActions(['editVideo', 'findVideos']),
    ...mapConversationsActions(['getChatHistory', 'checkFor10dlcRegister', 'getLeadsPlusUserInfo', 'checkLeadsPlusContact']),
    ...mapSoleProprietorACtions([
      'setShowRegistrationFlowModal',
      'setStep',
    ]),
    ...mapPaymentActions(['setAddRegisterFeeAddonsSuccess']),
    async handleVideoUpdate(videoUpdated) {
      // update both sources (videoLibrary and chatVideos)
      this.editVideo({ video: videoUpdated });
      this.closeSlideDrawer(false);

      // Refresh Chat data to reflect video updates
      await this.getChatHistory({ offset: 0, conversation: this.initialContactInfo });
    },
    handleReset() {
      if (this.$refs.videoLibrary) {
        this.$refs.videoLibrary.reset();
      }
    },
    toggleMessageDetail(newValue) {
      this.showVideoDetail = !!newValue;
    },
    insertLdVideo(selectedVideo) {
      this.$refs.messageField.insertLDContent(this.formatInsertVideo(selectedVideo));
      this.closeSlideDrawer(false);
    },
    /**
     * Helper function to generate the template object that we want to update
     * @param {Number} templateFolderId, the desired template folder id
     */
    generateTemplateObject(templateFolderId) {
      return {
        id: this.desiredTemplate.id,
        template_folder_id: templateFolderId,
        body: this.textMessage,
      };
    },
    /*
     * @param {Any}      [value] This is the desired value that we want to compare
     * @param {String}   [field] This is the desired field that we want to compare against
     * @return {Boolean} The boolean indicating whether a field has been edited or not
     *
     * This helper function is to check wheteher a value has changed or not.
     * As well as whether the desired field has been edited of not
     */
    isTemplateFieldChanged(value, field) {
      return (
        value &&
        this.desiredTemplate &&
        value !== this.desiredTemplate[field] &&
        this.isEditing === false
      );
    },

    // Send messages to the provided callback
    sendMessage(sendOptions) {
      const imagesURLs = this.getImagesURLsInSMS(this.textMessageElements.images, this.textMessage);
      this.sendingTextOptionsDialog = false;
      const message = {
        body: `${this.textMessage.replace(this.IMAGE_REGEX, ' ').trim()}`,
        contacts: [this.contactDetails],
        userTimezone: this.user.timezone,
        imagesURLs,
        sendOptions,
      };

      this.sendMessageCallback(message);
      this.textMessage = null;
      this.clearTextImages();
    },
    changeSendingOption(value) {
      this.sendOptionRadioValue = value;
    },
    onTemplateChange(newValue) {
      this.desiredTemplate = Object.assign({}, newValue);
    },
    async onSendClick() {
      this.sendingTextOptionsDialog = true;
    },
    onCloseRegisterBrandModal() {
      this.showRegisterBrandModal = false;
      if (this.canSendSMS) {
        this.sendingTextOptionsDialog = true;
      }
    },
    async onPending10DLCSend() {
      if (!this.registrationStatus10dlc && !this.isLeadsPlusContact) {
        this.setStep(STEPS_SOLE_PROPRIETOR.DECISION);
        this.setShowRegistrationFlowModal({ bool: true, action: solePropCloseAction });
        return;
      }

      if (this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_COMPLETED) {
        this.onSendClick();
        return;
      }

      if (this.shouldGoToLeadsPlusFlow && this.isLeadsPlusUser) {
        if (!this.isLeadsPlusContact || this.sendOptionRadioValue !== SEND_OPTIONS.Immediate) {
          this.setStep(STEPS_SOLE_PROPRIETOR.STATUS);
          this.setShowRegistrationFlowModal({ bool: true, action: null });
          return;
        }
        this.onSendClick();
        return;
      }

      if (
        this.registrationStatus10dlc === REGISTRATION_STATES.PAYMENT_PENDING ||
        this.registrationStatus10dlc === REGISTRATION_STATES.PAYMENT_CAMPAIGN_RETRY_PENDING ||
        this.registrationStatus10dlc === REGISTRATION_STATES.PAYMENT_BRAND_RETRY_PENDING
      ) {
        this.setStep(STEPS_SOLE_PROPRIETOR.PAYMENT);
        this.setShowRegistrationFlowModal({ bool: true, action: solePropCloseAction });
        return;
      }

      if (
        this.canSendSMS && (
          this.registrationStatus10dlc === REGISTRATION_STATES.BRAND_SUBMITTED ||
          this.registrationStatus10dlc === REGISTRATION_STATES.PAYMENT_RECEIVED ||
          this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_SUBMITTED ||
          this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_PENDING)
      ) {
        return this.onSendClick();
      }

      this.setStep(STEPS_SOLE_PROPRIETOR.STATUS);
      this.setShowRegistrationFlowModal({ bool: true, action: solePropCloseAction });
    },
  },
};
</script>
<style lang="scss" scoped>
.messages-list {
  --default-border-radius: 12px;
  height: calc(100vh - 430px);
  overflow-y: scroll;
  box-shadow: inset 0px 0px 1px rgba(0, 0, 0, 0.5);

  .missing__conversations {
    height: calc(100vh - 616px);
  }
}

.missing-messages {
  --default-border-radius: 12px;
  height: calc(100vh - 200px);
  overflow-y: scroll;
  box-shadow: inset 0px 0px 1px rgba(0, 0, 0, 0.5);

  .missing__conversations {
    height: calc(100vh - 316px);
  }
}

.text-editor {
  height: 20vh;
}

.action-button {
  font-size: 14px;
  padding: 6px;
}

.avatar-content {
  max-width: fit-content;
}

.main-card {
  box-shadow: none;
}
</style>
